import './FetchFilterBar.scss';
import { Category, FilterPaneBasicState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { MdOutlineArrowBackIos, MdOutlineArrowForwardIos } from 'react-icons/md';
import * as R from 'ramda';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Button } from '../Button/Button';
import { DropdownMenu } from '../Dropdown/DropdownMenu';
import { SegmentedControlButton } from '../SegmentedControlButton';
import { Appearance, Placement } from '../designConstants';

export const COMMON_CATEGORY_LABELS: Record<string, Record<string, string>> = {
  Avail: {
    L: 'Local',
    N: 'National'
  }
};

// false positive: isInvalid and invalidMsg are used via destructuring inside .map(), which the rule can't trace
// eslint-disable-next-line react/no-unused-prop-types
type CategoryWithValidity = Category & { isInvalid?: boolean; invalidMsg?: string };

interface FilterPaneBasicProps {
  categories: CategoryWithValidity[];
  initialOpenSection: string;
  readonly?: boolean;
  setState: Dispatch<SetStateAction<FilterPaneBasicState>>;
  state: FilterPaneBasicState;
  groupedCategories: Record<string, GroupedSegment[]> | undefined;
}

export const FilterPaneBasic = ({
  categories,
  initialOpenSection,
  readonly,
  setState,
  state,
  groupedCategories
}: FilterPaneBasicProps) => {
  const [openSection, setOpenSection] = useState(initialOpenSection);
  const { notMap, selectedMap } = state;

  const setNotMap = useCallback(
    (category: string, not: boolean) => {
      setState(
        R.mergeDeepLeft({
          notMap: {
            [category]: not
          }
        })
      );
    },
    [setState]
  );

  const setCheckBox = useCallback(
    (category: string, key: string, checked: boolean) => {
      setState(
        R.mergeDeepLeft({
          selectedMap: {
            [category]: {
              [key]: checked
            }
          }
        })
      );
    },
    [setState]
  );

  useEffect(() => {
    const filteredSelectedMap = { ...selectedMap };
    for (const key of R.keys(filteredSelectedMap)) {
      for (const options of R.keys(filteredSelectedMap[key])) {
        if (!filteredSelectedMap[key][options]) {
          delete filteredSelectedMap[key][options];
        }
      }
      if (!R.keys(filteredSelectedMap[key]).length) {
        delete filteredSelectedMap[key];
      }
    }
    if (R.keys(filteredSelectedMap).length !== R.keys(selectedMap).length) {
      setState(prev => ({
        ...prev,
        selectedMap: filteredSelectedMap
      }));
    }
  }, [selectedMap, setState]);

  useEffect(() => {
    const filteredNotMap = { ...notMap };
    for (const key of R.keys(filteredNotMap)) {
      if (!filteredNotMap[key]) {
        delete filteredNotMap[key];
      }
    }
    if (R.keys(filteredNotMap).length !== R.keys(notMap).length) {
      setState(prev => ({
        ...prev,
        notMap: filteredNotMap
      }));
    }
  }, [notMap, selectedMap, setState]);

  const [subFilterMap, setSubFilterMap] = useState<
    Record<string, ((value: object) => boolean) | undefined>
  >({});

  const filteredCategories = useMemo(() => {
    if (!groupedCategories) {
      return categories;
    }
    return [
      ...categories.filter(
        category =>
          !Object.keys(groupedCategories).some(gc =>
            groupedCategories[gc].some(g => g.segmentName === category.key)
          )
      ),
      ...Object.keys(groupedCategories).map((groupName): CategoryWithValidity => ({
        label: groupName,
        key: groupName,
        options: groupedCategories[groupName].map(groupedCategory => ({
          label: groupedCategory.segmentName,
          value: groupedCategory.segmentName
        })),
        isCustomSegment: true
      }))
    ];
  }, [categories, groupedCategories]);

  return (
    <div className='filter-pane-body'>
      {filteredCategories.map(
        ({ label, key: category, options, isInvalid, invalidMsg }: CategoryWithValidity) => {
          let content: ReactElement;
          if (groupedCategories && Object.keys(groupedCategories).includes(label)) {
            const sortedOptions = options.sort();
            const expanded = openSection === category;
            const not = options.map(option =>
              typeof option === 'string' ? notMap[option] : notMap[option.label]
            );
            const selectedItemMap = options.map(option => {
              const value = typeof option === 'string' ? option : option.value;
              return selectedMap[value] ? { [value]: true } : {};
            });
            const selectedOptions: typeof sortedOptions = [];
            for (const option of sortedOptions) {
              const key = typeof option === 'string' ? option : option.value;
              if (selectedItemMap[key]) {
                selectedOptions.push(option);
              }
            }
            if (readonly && !selectedOptions.length) {
              return null;
            }
            const excludeClasses = ['not'];
            if (not) {
              excludeClasses.push('selected');
            }
            if (readonly) {
              excludeClasses.push('disabled');
            }
            content = (
              <div key={category} className={`category-container ${isInvalid ? 'disabled' : ''}`}>
                <div className='category-header'>
                  <button
                    type='button'
                    className='category-header-toggle'
                    disabled={Boolean(isInvalid) || Boolean(readonly)}
                    onClick={() => setOpenSection(expanded ? '' : category)}
                  >
                    <div className='header-label'>{label}</div>
                  </button>
                  {expanded && (
                    <div className='include-exclude-toggle-button-container'>
                      <SegmentedControlButton
                        className={excludeClasses.join(' ')}
                        onChange={option =>
                          options.forEach(opt =>
                            setNotMap(
                              typeof opt === 'string' ? opt : opt.label,
                              readonly
                                ? notMap[typeof opt === 'string' ? opt : opt.label]
                                : option === 'Exclude'
                            )
                          )
                        }
                        options={['Include', 'Exclude']}
                        selectedOption={not.some(val => val === true) ? 'Exclude' : 'Include'}
                      />
                    </div>
                  )}
                  {!readonly && (
                    <button
                      type='button'
                      className='expander'
                      disabled={Boolean(isInvalid)}
                      onClick={() => setOpenSection(expanded ? '' : category)}
                    >
                      {expanded ? <MdOutlineArrowBackIos /> : <MdOutlineArrowForwardIos />}
                    </button>
                  )}
                </div>
                {expanded && !readonly && (
                  <div className='category-options'>
                    <div className='select-all-buttons'>
                      <Button
                        className='select-all'
                        appearance={Appearance.EMPTY}
                        onClick={() => {
                          const mappedOptions = sortedOptions.map(option => {
                            let value: string;
                            let optLabel = '';
                            if (typeof option === 'string') {
                              value = option;
                            } else {
                              optLabel = option.label;
                              value = option.value;
                            }
                            return {
                              label: optLabel || value || '(none)',
                              value
                            };
                          });
                          const filter = subFilterMap[category];
                          const updates: Record<string, { Yes: true }> = {};
                          mappedOptions.forEach(option => {
                            if (filter) {
                              if (filter(option)) {
                                updates[option.value] = { Yes: true };
                              }
                            } else {
                              updates[option.value] = { Yes: true };
                            }
                          });
                          setState(R.mergeDeepLeft({ selectedMap: updates }));
                        }}
                      >
                        Select All
                      </Button>
                      <Button
                        className='select-all'
                        appearance={Appearance.EMPTY}
                        onClick={() => {
                          const updates: Record<string, Record<string, boolean>> = {};
                          for (const option of sortedOptions) {
                            const value = typeof option === 'string' ? option : option.value;
                            updates[value] = {};
                          }
                          setState(prev => ({
                            ...prev,
                            selectedMap: { ...prev.selectedMap, ...updates }
                          }));
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    <DropdownMenu
                      applyOnClick={null}
                      cancelOnClick={null}
                      hasFilterBar={sortedOptions.length > 10}
                      menuItems={(
                        sortedOptions as (string | { label: string; value: string })[]
                      ).map(option => {
                        let optLabel: string;
                        let value: string;
                        if (typeof option === 'string') {
                          optLabel = option;
                          value = option;
                        } else {
                          optLabel = option.label;
                          value = option.value;
                        }
                        const checked = R.path([value, 'Yes'], selectedMap) as boolean;
                        const commonLabel =
                          COMMON_CATEGORY_LABELS &&
                          COMMON_CATEGORY_LABELS[category] &&
                          COMMON_CATEGORY_LABELS[category][value]
                            ? COMMON_CATEGORY_LABELS[category][value]
                            : undefined;
                        return {
                          label: commonLabel || optLabel || value || '(none)',
                          selected: checked,
                          value
                        };
                      })}
                      multiSelect
                      itemOnClick={item => {
                        if (!readonly) {
                          const { value } = item;
                          const checked = R.path([value, 'Yes'], selectedMap) as boolean;
                          setCheckBox(value, 'Yes', !checked);
                        }
                      }}
                      onFilter={filters =>
                        setSubFilterMap(prevMap => ({ ...prevMap, [category]: filters[0] }))
                      }
                      standalone
                    />
                  </div>
                )}
              </div>
            );
          } else {
            const sortedOptions = options.sort();
            const expanded = openSection === category;
            const not = notMap[category];
            const selectedItemMap = selectedMap[category] || {};
            const selectedOptions: typeof sortedOptions = [];
            for (const option of sortedOptions) {
              const key = typeof option === 'string' ? option : option.value;
              if (selectedItemMap[key]) {
                selectedOptions.push(option);
              }
            }
            if (readonly && !selectedOptions.length) {
              return null;
            }
            const excludeClasses = ['not'];
            if (not) {
              excludeClasses.push('selected');
            }
            if (readonly) {
              excludeClasses.push('disabled');
            }
            content = (
              <div key={category} className={`category-container ${isInvalid ? 'disabled' : ''}`}>
                <div className='category-header'>
                  <button
                    type='button'
                    className='category-header-toggle'
                    disabled={Boolean(isInvalid) || Boolean(readonly)}
                    onClick={() => setOpenSection(expanded ? '' : category)}
                  >
                    <div className='header-label'>{label}</div>
                  </button>
                  {expanded && (
                    <div className='include-exclude-toggle-button-container'>
                      <SegmentedControlButton
                        className={excludeClasses.join(' ')}
                        onChange={option =>
                          setNotMap(category, readonly ? not : option === 'Exclude')
                        }
                        options={['Include', 'Exclude']}
                        selectedOption={notMap[category] ? 'Exclude' : 'Include'}
                      />
                    </div>
                  )}
                  {!readonly && (
                    <button
                      type='button'
                      className='expander'
                      disabled={Boolean(isInvalid)}
                      onClick={() => setOpenSection(expanded ? '' : category)}
                    >
                      {expanded ? <MdOutlineArrowBackIos /> : <MdOutlineArrowForwardIos />}
                    </button>
                  )}
                </div>
                {expanded && !readonly && (
                  <div className='category-options'>
                    <div className='select-all-buttons'>
                      <Button
                        className='select-all'
                        appearance={Appearance.EMPTY}
                        onClick={() => {
                          const checkList: Record<string, true> = {};
                          const mappedOptions = sortedOptions.map(option => {
                            let value: string;
                            let optLabel = '';
                            if (typeof option === 'string') {
                              value = option;
                            } else {
                              optLabel = option.label;
                              value = option.value;
                            }
                            return {
                              label: optLabel || value || '(none)',
                              value
                            };
                          });
                          const filter = subFilterMap[category];
                          mappedOptions.forEach(option => {
                            if (filter) {
                              if (filter(option)) {
                                checkList[option.value] = true;
                              }
                            } else {
                              checkList[option.value] = true;
                            }
                          });
                          setState(
                            R.mergeDeepLeft({
                              selectedMap: {
                                [category]: checkList
                              }
                            })
                          );
                        }}
                      >
                        Select All
                      </Button>
                      <Button
                        className='select-all'
                        appearance={Appearance.EMPTY}
                        onClick={() => {
                          setState(prev => ({
                            ...prev,
                            selectedMap: { ...prev.selectedMap, [category]: {} }
                          }));
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    <DropdownMenu
                      applyOnClick={null}
                      cancelOnClick={null}
                      hasFilterBar={sortedOptions.length > 10}
                      menuItems={(
                        sortedOptions as (string | { label: string; value: string })[]
                      ).map(option => {
                        let optLabel: string;
                        let value: string;
                        if (typeof option === 'string') {
                          optLabel = option;
                          value = option;
                        } else {
                          optLabel = option.label;
                          value = option.value;
                        }
                        const checked = R.path([category, value], selectedMap) as boolean;
                        const commonLabel =
                          COMMON_CATEGORY_LABELS &&
                          COMMON_CATEGORY_LABELS[category] &&
                          COMMON_CATEGORY_LABELS[category][value]
                            ? COMMON_CATEGORY_LABELS[category][value]
                            : undefined;
                        return {
                          label: commonLabel || optLabel || value || '(none)',
                          selected: checked,
                          value
                        };
                      })}
                      multiSelect
                      itemOnClick={item => {
                        if (!readonly) {
                          const { value } = item;
                          const checked = R.path([category, value], selectedMap) as boolean;
                          setCheckBox(category, item.value, !checked);
                        }
                      }}
                      onFilter={filters =>
                        setSubFilterMap(prevMap => ({ ...prevMap, [category]: filters[0] }))
                      }
                      standalone
                    />
                  </div>
                )}
              </div>
            );
          }
          return isInvalid && invalidMsg ? (
            <OverlayTrigger
              key={`basicFilterPaneTooltipOverlay${category}`}
              placement={Placement.BOTTOM}
              overlay={<Tooltip id={`tooltip-${category}`}>{invalidMsg}</Tooltip>}
            >
              {content}
            </OverlayTrigger>
          ) : (
            content
          );
        }
      )}
    </div>
  );
};
